// Saves generated project documents (GIZ timesheets, project sheets) into the project folders of the
// "INT-EE - Documents" library on SharePoint, using the signed-in user's Graph token (the app already
// requests Files.ReadWrite.All / Sites.ReadWrite.All at login).

// Switched off for now: generated files are only downloaded. Set to true to also store them in the
// project folder on SharePoint (the upload code below is kept for that).
export const SHAREPOINT_SAVE_ENABLED = false;

const GRAPH = 'https://graph.microsoft.com/v1.0';
const SHAREPOINT_HOST = 'integrationconsultants.sharepoint.com';
// Site of the synced "INTEGRATION GmbH/INT-EE - Documents" folder; found via search if the path differs.
const PROJECT_SITE_PATH = '/sites/INT-EE';
const LIBRARY_MARKER = 'INT-EE - Documents';

let cachedDriveId: string | null = null;

const token = () => localStorage.getItem('accessToken') || '';

async function graph<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(path.startsWith('http') ? path : `${GRAPH}${path}`, {
        ...init,
        headers: { Authorization: `Bearer ${token()}`, ...(init?.headers || {}) },
    });
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message || `SharePoint request failed (${response.status})`);
    }
    return response.json() as Promise<T>;
}

// Document library (drive) that holds the "Projects" folder.
export async function getProjectDriveId(): Promise<string> {
    if (cachedDriveId) return cachedDriveId;
    let siteId: string | null = null;
    try {
        siteId = (await graph<{ id: string }>(`/sites/${SHAREPOINT_HOST}:${PROJECT_SITE_PATH}`)).id;
    } catch {
        const found = await graph<{ value: { id: string; displayName: string }[] }>(`/sites?search=${encodeURIComponent('INT-EE')}`);
        siteId = found.value[0]?.id ?? null;
    }
    if (!siteId) throw new Error('Could not find the INT-EE SharePoint site.');
    cachedDriveId = (await graph<{ id: string }>(`/sites/${siteId}/drive`)).id;
    return cachedDriveId;
}

// "%HOMEDRIVE%%HOMEPATH%\INTEGRATION GmbH\INT-EE - Documents\Projects\6066 GIZ ..." -> "Projects/6066 GIZ ..."
export function libraryPathFromFolder(folder: string | null | undefined): string | null {
    if (!folder) return null;
    const normalised = folder.replace(/\\/g, '/');
    const index = normalised.indexOf(LIBRARY_MARKER);
    if (index < 0) return null;
    const rest = normalised.slice(index + LIBRARY_MARKER.length).replace(/^\/+|\/+$/g, '');
    return rest || null;
}

// Finds "Projects/<number> ..." by the project number when the project has no folder stored.
export async function findProjectFolder(projectNumber: string): Promise<string | null> {
    const driveId = await getProjectDriveId();
    let url: string | null = `/drives/${driveId}/root:/Projects:/children?$select=name,folder&$top=999`;
    while (url) {
        const page: { value: { name: string; folder?: object }[]; '@odata.nextLink'?: string } = await graph(url);
        const match = page.value.find((item) => item.folder && (item.name === projectNumber || item.name.startsWith(`${projectNumber} `)));
        if (match) return `Projects/${match.name}`;
        url = page['@odata.nextLink'] ?? null;
    }
    return null;
}

const encodePath = (path: string) => path.split('/').filter(Boolean).map(encodeURIComponent).join('/');

// Uploads (or replaces) a file; missing folders are created by Graph. Returns the file's web URL.
export async function uploadProjectFile(folderPath: string, fileName: string, content: Blob): Promise<string> {
    const driveId = await getProjectDriveId();
    const item = await graph<{ webUrl: string }>(`/drives/${driveId}/root:/${encodePath(folderPath)}/${encodeURIComponent(fileName)}:/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        body: content,
    });
    return item.webUrl;
}

export function downloadBlob(content: Blob, fileName: string) {
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
